import React from 'react';
import { Box, Button, Container, Typography } from '@mui/material';

// Route-level error boundary. Catches render/runtime errors in a route subtree
// so a single page failure never takes down the whole app shell. Used to wrap
// route-level components (e.g. the shift-report review page) per the web app
// conventions in AGENTS.md.

interface ErrorBoundaryProps {
  children: React.ReactNode;
  fallbackTitle?: string;
}

interface ErrorBoundaryState {
  hasError: boolean;
  message: string | null;
}

export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, message: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, message: error.message };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo): void {
    // eslint-disable-next-line no-console
    console.error('Route error boundary caught an error:', error, info.componentStack);
  }

  private handleReset = (): void => {
    this.setState({ hasError: false, message: null });
  };

  render(): React.ReactNode {
    if (this.state.hasError) {
      return (
        <Container maxWidth="sm" sx={{ py: 6 }}>
          <Box data-testid="route-error-boundary" sx={{ textAlign: 'center' }}>
            <Typography variant="h5" gutterBottom>
              {this.props.fallbackTitle ?? 'Something went wrong'}
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              {this.state.message ?? 'An unexpected error occurred while loading this page.'}
            </Typography>
            <Button variant="contained" onClick={this.handleReset}>
              Try again
            </Button>
          </Box>
        </Container>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
